import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CloseIcon from "@material-ui/icons/Close";
import { useHistory, useParams } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import PageTitle from "../../components/PageTitle/PageTitle";
import CalenderAssignmentService from "./Locality/Service/calenderAssignmentService";
import ImagesStoreService from "./Locality/Service/imagesStoreService";

const useStyles = makeStyles((theme) => ({
  layout: {
    minHeight: 420,
  },
  leftPanel: {
    height: "100%",
    minHeight: 420,
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
  leftHeader: {
    padding: theme.spacing(2),
    backgroundColor: "#0f3b62",
    color: "#fff",
  },
  classTitle: {
    fontWeight: 700,
    fontSize: "1.1rem",
    lineHeight: 1.3,
  },
  sectionSubtitle: {
    fontSize: "0.875rem",
    opacity: 0.9,
    marginTop: theme.spacing(0.5),
    paddingLeft: theme.spacing(1.5),
    borderLeft: "3px solid rgba(255,255,255,0.5)",
  },
  eventList: {
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(1, 0),
  },
  eventListItem: {
    borderRadius: 8,
    margin: theme.spacing(0.25, 1),
    "&.Mui-selected": {
      backgroundColor: "rgba(15, 59, 98, 0.12)",
      color: "#0f3b62",
    },
    "&.Mui-selected:hover": {
      backgroundColor: "rgba(15, 59, 98, 0.18)",
    },
  },
  rightPanel: {
    minHeight: 420,
    borderRadius: 12,
    border: `1px solid ${theme.palette.divider}`,
    display: "flex",
    flexDirection: "column",
  },
  rightHeader: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.grey[50],
  },
  imageGrid: {
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(2),
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
    gap: theme.spacing(2),
    alignContent: "start",
  },
  imageCard: {
    position: "relative",
    borderRadius: 10,
    overflow: "hidden",
    cursor: "pointer",
    border: "2px solid transparent",
    transition: "border-color 0.15s, box-shadow 0.15s",
    "&:hover": {
      boxShadow: theme.shadows[2],
    },
  },
  imageCardSelected: {
    borderColor: "#0f3b62",
    boxShadow: "0 0 0 2px rgba(15, 59, 98, 0.25)",
  },
  imageCardSaved: {
    borderColor: "#2e7d32",
  },
  imageCardWillRemove: {
    borderColor: "#ed6c02",
    opacity: 0.75,
  },
  unselectBtn: {
    position: "absolute",
    top: 4,
    right: 4,
    backgroundColor: "rgba(255,255,255,0.95)",
    padding: 4,
    zIndex: 2,
    "&:hover": {
      backgroundColor: "#ffebee",
    },
  },
  selectedStrip: {
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: "#fff",
    maxHeight: 120,
    overflowY: "auto",
  },
  selectedChip: {
    display: "inline-flex",
    alignItems: "center",
    margin: theme.spacing(0.25),
    borderRadius: 8,
    border: "1px solid #e2e8f0",
    overflow: "hidden",
  },
  selectedChipThumb: {
    width: 40,
    height: 40,
    objectFit: "cover",
  },
  outlineBtn: {
    borderColor: "#0f3b62",
    color: "#0f3b62",
  },
  thumb: {
    width: "100%",
    height: 110,
    objectFit: "cover",
    display: "block",
  },
  imageLabel: {
    padding: theme.spacing(0.75, 1),
    fontSize: "0.7rem",
    backgroundColor: "#fff",
    color: theme.palette.text.secondary,
    textAlign: "center",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  checkBadge: {
    position: "absolute",
    top: 6,
    right: 6,
    color: "#0f3b62",
    backgroundColor: "#fff",
    borderRadius: "50%",
  },
  savedBadge: {
    position: "absolute",
    top: 6,
    left: 6,
    fontSize: "0.65rem",
    fontWeight: 700,
    backgroundColor: "#2e7d32",
    color: "#fff",
    padding: "2px 6px",
    borderRadius: 4,
  },
  saveBar: {
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    backgroundColor: "#fff",
    borderRadius: "0 0 12px 12px",
  },
  primaryBtn: {
    backgroundColor: "rgb(15 59 98)",
    color: "#fff",
    minWidth: 140,
    "&:hover": {
      backgroundColor: "rgb(12 47 78)",
    },
  },
}));

const isMongoId = (value) => /^[a-f\d]{24}$/i.test(String(value || ""));

const resolveDisplayName = (value, nameKey) => {
  if (!value) return "";
  if (typeof value === "object") {
    return value[nameKey] || value.className || value.sectionName || "";
  }
  if (isMongoId(value)) return "";
  return String(value);
};

const normalizeId = (value) => {
  if (!value) return "";
  if (typeof value === "object") return value._id || "";
  return String(value);
};

const buildUpdatePayload = (event, imageURLs) => {
  const studentIds = Array.isArray(event.studentId)
    ? event.studentId.map((s) => normalizeId(s))
    : event.studentId
      ? [normalizeId(event.studentId)]
      : [];

  return {
    _id: event._id,
    schooleId: normalizeId(event.schooleId),
    classId: normalizeId(event.classId),
    sectionId: normalizeId(event.sectionId) || null,
    eventName: event.eventName,
    imageURL: imageURLs,
    observation: event.observation || "",
    video: event.video || "",
    date: event.date,
    studentId: studentIds.length ? studentIds : null,
    isEventForAllStudent: Boolean(event.isEventForAllStudent),
    iconId: normalizeId(event.iconId) || null,
  };
};

const ALL_EVENTS_KEY = "__all__";

export default function CalenderAssignmentAddImages() {
  const classes = useStyles();
  const history = useHistory();
  const { eventId } = useParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [calendarEvent, setCalendarEvent] = useState(null);
  const [imagesStoreRows, setImagesStoreRows] = useState([]);
  const [savedImageUrls, setSavedImageUrls] = useState([]);
  const [selectedUrls, setSelectedUrls] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState(ALL_EVENTS_KEY);
  const [error, setError] = useState("");

  const loadData = useCallback(async () => {
    if (!eventId) {
      setError("Calendar event not found.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const userDetails = JSON.parse(localStorage.getItem("userDetail") || "{}");
      const eventRes = await CalenderAssignmentService.getCalenderAssignmentbyId(eventId);
      const event = eventRes?.data || eventRes;

      if (!event?._id) {
        setError("Calendar event not found.");
        setLoading(false);
        return;
      }

      setCalendarEvent(event);
      const existing = Array.isArray(event.imageURL) ? event.imageURL.filter(Boolean) : [];
      setSavedImageUrls(existing);
      setSelectedUrls([...existing]);

      const classId = normalizeId(event.classId);
      if (!classId || !userDetails.schooleId) {
        setImagesStoreRows([]);
        setLoading(false);
        return;
      }

      const body = { schooleId: userDetails.schooleId, classId };
      const sectionId = normalizeId(event.sectionId);
      if (sectionId) body.sectionId = sectionId;

      const storeRes = await ImagesStoreService.findImagesStoreList(body);
      setImagesStoreRows(Array.isArray(storeRes) ? storeRes : []);
      setSelectedStoreId(ALL_EVENTS_KEY);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || err?.message || "Failed to load data.");
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const calendarClassName = resolveDisplayName(calendarEvent?.classId, "className");
  const calendarSectionName = resolveDisplayName(calendarEvent?.sectionId, "sectionName");
  const calendarEventName = calendarEvent?.eventName || "Calendar Event";

  const visibleImages = useMemo(() => {
    const rows =
      selectedStoreId === ALL_EVENTS_KEY
        ? imagesStoreRows
        : imagesStoreRows.filter((row) => row._id === selectedStoreId);

    return rows.flatMap((row) =>
      (row.imageURL || [])
        .map((url) => ({
          url: String(url || "").trim(),
          storeEventName: row.eventName || "—",
          storeRecordId: row._id,
        }))
        .filter((item) => item.url),
    );
  }, [imagesStoreRows, selectedStoreId]);

  const toggleImageSelection = (url) => {
    setSelectedUrls((prev) =>
      prev.includes(url) ? prev.filter((u) => u !== url) : [...prev, url],
    );
  };

  const clearSelection = () => {
    setSelectedUrls([]);
  };

  const hasSelectionChanges = useMemo(() => {
    const a = [...savedImageUrls].sort().join("|");
    const b = [...selectedUrls].sort().join("|");
    return a !== b;
  }, [savedImageUrls, selectedUrls]);

  const handleSave = async () => {
    if (!calendarEvent) return;

    if (!hasSelectionChanges) {
      alert("No changes to save.");
      return;
    }

    setSaving(true);
    try {
      const nextUrls = [...selectedUrls];
      const payload = buildUpdatePayload(calendarEvent, nextUrls);
      await CalenderAssignmentService.upadeCalenderAssignment(payload);
      setSavedImageUrls(nextUrls);
      setSelectedUrls([...nextUrls]);
      setCalendarEvent((prev) => ({ ...prev, imageURL: nextUrls }));
      window.dispatchEvent(
        new CustomEvent("calendarEventUpdated", { detail: { eventId: calendarEvent._id } }),
      );
      alert("Images saved successfully.");
      history.push("/app/calenderassignment");
    } catch (err) {
      alert(err?.response?.data?.message || err?.message || "Failed to save images.");
    } finally {
      setSaving(false);
    }
  };

  const totalSelected = selectedUrls.length;
  const totalSaved = savedImageUrls.length;
  const newlySelectedCount = selectedUrls.filter((u) => !savedImageUrls.includes(u)).length;
  const removedCount = savedImageUrls.filter((u) => !selectedUrls.includes(u)).length;
  const pageTitlePrefix = totalSaved > 0 ? "Edit Images" : "Add Images";

  return (
    <>
      <PageTitle
        title={`${pageTitlePrefix} — ${calendarEventName}`}
        button={
          <Button
            variant="contained"
            className={classes.primaryBtn}
            startIcon={<ArrowBackIcon />}
            onClick={() => history.push("/app/calenderassignment")}
          >
            Back
          </Button>
        }
      />

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={280}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error" style={{ padding: 16 }}>
          {error}
        </Typography>
      ) : (
        <Paper elevation={0} className={classes.layout}>
          <Grid container>
            {/* Left — Class & Section + event list */}
            <Grid item xs={12} md={3}>
              <Box className={classes.leftPanel}>
                <Box className={classes.leftHeader}>
                  {calendarClassName ? (
                    <Typography className={classes.classTitle}>
                      {calendarClassName}
                    </Typography>
                  ) : null}
                  {calendarSectionName ? (
                    <Typography className={classes.sectionSubtitle}>
                      Section: {calendarSectionName}
                    </Typography>
                  ) : null}
                </Box>
                <Divider />
                <List className={classes.eventList} disablePadding>
                  <ListItem
                    button
                    selected={selectedStoreId === ALL_EVENTS_KEY}
                    className={classes.eventListItem}
                    onClick={() => setSelectedStoreId(ALL_EVENTS_KEY)}
                  >
                    <ListItemText
                      primary="All Images"
                      secondary={`${imagesStoreRows.reduce(
                        (n, r) => n + (r.imageURL?.length || 0),
                        0,
                      )} images`}
                    />
                  </ListItem>
                  {imagesStoreRows.map((row) => {
                    const count = (row.imageURL || []).length;
                    return (
                      <ListItem
                        key={row._id}
                        button
                        selected={selectedStoreId === row._id}
                        className={classes.eventListItem}
                        onClick={() => setSelectedStoreId(row._id)}
                      >
                        <ListItemText
                          primary={row.eventName || "—"}
                          secondary={`${count} image${count !== 1 ? "s" : ""}`}
                        />
                      </ListItem>
                    );
                  })}
                  {imagesStoreRows.length === 0 ? (
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      style={{ padding: 16, display: "block" }}
                    >
                      No Image Store records for this class
                      {normalizeId(calendarEvent?.sectionId) ? " and section" : ""}.
                    </Typography>
                  ) : null}
                </List>
              </Box>
            </Grid>

            {/* Right — Image grid */}
            <Grid item xs={12} md={9}>
              <Box className={classes.rightPanel}>
                <Box className={classes.rightHeader}>
                  <Typography variant="subtitle1" style={{ fontWeight: 700, color: "#0f3b62" }}>
                    {selectedStoreId === ALL_EVENTS_KEY
                      ? "All images"
                      : imagesStoreRows.find((r) => r._id === selectedStoreId)?.eventName ||
                        "Images"}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Click an image to select. Click again or use ✕ to unselect.
                  </Typography>
                  {totalSelected > 0 ? (
                    <Button
                      size="small"
                      variant="outlined"
                      className={classes.outlineBtn}
                      style={{ marginTop: 8 }}
                      onClick={clearSelection}
                    >
                      Clear selection
                    </Button>
                  ) : null}
                </Box>

                {totalSelected > 0 ? (
                  <Box className={classes.selectedStrip}>
                    <Typography variant="caption" style={{ fontWeight: 600, display: "block", marginBottom: 6 }}>
                      Selected ({totalSelected}) — click ✕ to remove
                    </Typography>
                    <Box display="flex" flexWrap="wrap">
                      {selectedUrls.map((url) => (
                        <Box key={url} className={classes.selectedChip}>
                          <img src={url} alt="" className={classes.selectedChipThumb} />
                          <IconButton
                            size="small"
                            aria-label="Unselect image"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleImageSelection(url);
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                ) : null}

                <Box className={classes.imageGrid}>
                  {visibleImages.length === 0 ? (
                    <Typography color="textSecondary" style={{ gridColumn: "1 / -1" }}>
                      No images to display.
                    </Typography>
                  ) : (
                    visibleImages.map((item, index) => {
                      const isSelected = selectedUrls.includes(item.url);
                      const wasSaved = savedImageUrls.includes(item.url);
                      const willRemove = wasSaved && !isSelected;
                      const isNewlySelected = isSelected && !wasSaved;
                      const cardClass = [
                        classes.imageCard,
                        isSelected && wasSaved ? classes.imageCardSaved : "",
                        isNewlySelected ? classes.imageCardSelected : "",
                        willRemove ? classes.imageCardWillRemove : "",
                      ]
                        .filter(Boolean)
                        .join(" ");

                      return (
                        <Box
                          key={`${item.storeRecordId}-${item.url}-${index}`}
                          className={cardClass}
                          onClick={() => toggleImageSelection(item.url)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              toggleImageSelection(item.url);
                            }
                          }}
                        >
                          {wasSaved && isSelected ? (
                            <span className={classes.savedBadge}>Saved</span>
                          ) : null}
                          {willRemove ? (
                            <span className={classes.savedBadge} style={{ backgroundColor: "#ed6c02" }}>
                              Remove
                            </span>
                          ) : null}
                          {isSelected ? (
                            <IconButton
                              size="small"
                              className={classes.unselectBtn}
                              aria-label="Unselect"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleImageSelection(item.url);
                              }}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          ) : null}
                          {isSelected && !willRemove ? (
                            <CheckCircleIcon className={classes.checkBadge} fontSize="small" />
                          ) : null}
                          <img
                            src={item.url}
                            alt={item.storeEventName}
                            className={classes.thumb}
                          />
                          <Typography className={classes.imageLabel}>
                            {item.storeEventName}
                          </Typography>
                        </Box>
                      );
                    })
                  )}
                </Box>

                <Box className={classes.saveBar}>
                  <Box>
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                      Selected: {totalSelected}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" display="block">
                      On event: {totalSaved}
                      {newlySelectedCount > 0 ? ` · +${newlySelectedCount} new` : ""}
                      {removedCount > 0 ? ` · −${removedCount} to remove` : ""}
                    </Typography>
                  </Box>
                  <Box display="flex" style={{ gap: 8, flexWrap: "wrap" }}>
                    <Button
                      variant="outlined"
                      className={classes.outlineBtn}
                      disabled={totalSelected === 0}
                      onClick={clearSelection}
                    >
                      Clear selection
                    </Button>
                    <Button
                      variant="contained"
                      className={classes.primaryBtn}
                      disabled={saving || !hasSelectionChanges}
                      onClick={handleSave}
                      startIcon={saving ? <CircularProgress size={18} color="inherit" /> : null}
                    >
                      {saving ? "Saving…" : "Save"}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}
    </>
  );
}
